import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { googleFontFamily } from "@babackapp/editor-blocks-library/fonts";
import type { EmailsThemeDto } from "@babackapp/private-api-client/types";
import { cn } from "@babackapp/ui/lib/utils";
import { useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  customGoogleFontTheme,
  emailFontChoice,
  GOOGLE_EMAIL_FONTS,
  googleFontTheme,
  STANDARD_EMAIL_FONTS,
  standardFontTheme,
} from "../lib/email-fonts";

type EmailsThemeFormValues = { theme?: EmailsThemeDto | null };

/**
 * Colors and radius of an emails theme. Shared by the tracking theme
 * (track-office settings) and the return theme (return templates editor):
 * the host decides where the theme is saved.
 */
export function EmailsThemeForm({
  theme,
  onSubmit,
}: {
  theme?: EmailsThemeDto | null;
  onSubmit: (theme: EmailsThemeDto) => Promise<unknown>;
}) {
  const { t } = useTranslation("trackoffice");

  const form = useForm<EmailsThemeFormValues>({
    defaultValues: { theme },
  });

  const submit = form.handleSubmit(async (values) => {
    await onSubmit(values.theme!);
  });

  const isDirty = form.formState.isDirty;
  // A Google font name being typed: saving would keep the previous font.
  const [isFontInvalid, setIsFontInvalid] = useState(false);
  const [fontFamily, fontUrl] = useWatch({
    control: form.control,
    name: ["theme.fontFamily", "theme.fontUrl"],
  });

  const setFont = (font: Pick<EmailsThemeDto, "fontFamily" | "fontUrl">) => {
    form.setValue("theme.fontFamily", font.fontFamily, { shouldDirty: true });
    form.setValue("theme.fontUrl", font.fontUrl, { shouldDirty: true });
  };

  return (
    <div className="">
      <div className="grid grid-cols-2 gap-3 py-4">
        <Controller
          control={form.control}
          name="theme.primaryColor"
          render={({ field }) => (
            <SettingsCard width="half">
              <ColorSwitcher
                title={t("settings.templates.primary-color", "Primary color")}
                description={t(
                  "settings.templates.primary-color-desc",
                  "Buttons, links and main accents",
                )}
                value={field.value!}
                onChange={field.onChange}
                type="simple"
              />
            </SettingsCard>
          )}
        />
        <Controller
          control={form.control}
          name="theme.secondaryColor"
          render={({ field }) => (
            <SettingsCard width="half">
              <ColorSwitcher
                title={t(
                  "settings.templates.secondary-color",
                  "Secondary color",
                )}
                description={t(
                  "settings.templates.secondary-color-desc",
                  "Buttons and intermediate accents",
                )}
                value={field.value!}
                onChange={field.onChange}
                type="simple"
              />
            </SettingsCard>
          )}
        />
        <Controller
          control={form.control}
          name="theme.backgroundColor"
          render={({ field }) => (
            <SettingsCard width="half">
              <ColorSwitcher
                title={t(
                  "settings.templates.background-color",
                  "Background color",
                )}
                description={t(
                  "settings.templates.background-color-desc",
                  "Background of your emails",
                )}
                value={field.value!}
                onChange={field.onChange}
                type="background"
              />
            </SettingsCard>
          )}
        />
        <Controller
          control={form.control}
          name="theme.cardColor"
          render={({ field }) => (
            <SettingsCard width="half">
              <ColorSwitcher
                title={t("settings.templates.card-color", "Card color")}
                description={t(
                  "settings.templates.card-color-desc",
                  "Cards and sections of your emails",
                )}
                value={field.value!}
                onChange={field.onChange}
                type="card"
              />
            </SettingsCard>
          )}
        />
        <Controller
          control={form.control}
          name="theme.radius"
          render={({ field }) => (
            <SettingsCard className="col-span-2">
              <BorderRadiusSelector
                title={t("settings.templates.border-radius", "Border radius")}
                description={t(
                  "settings.templates.border-radius-desc",
                  "The roundness of card and button corners",
                )}
                value={field.value || "default"}
                onChange={field.onChange}
              />
            </SettingsCard>
          )}
        />
        <SettingsCard className="col-span-2">
          <FontSelector
            title={t("settings.templates.font", "Font")}
            description={t(
              "settings.templates.font-desc",
              "Gmail and Outlook on Windows don't load Google Fonts: they show the closest standard font instead.",
            )}
            fontFamily={fontFamily}
            fontUrl={fontUrl}
            onChange={setFont}
            onInvalidChange={setIsFontInvalid}
          />
        </SettingsCard>
      </div>
      <div className="flex justify-end">
        <Button onClick={submit} disabled={!isDirty || isFontInvalid}>
          {t("settings.templates.apply", "Apply")}
        </Button>
      </div>
    </div>
  );
}

interface FontSelectorProps {
  title: string;
  description: string;
  fontFamily?: string | null;
  fontUrl?: string | null;
  onChange: (font: Pick<EmailsThemeDto, "fontFamily" | "fontUrl">) => void;
  onInvalidChange: (invalid: boolean) => void;
}

function FontSelector({
  title,
  description,
  fontFamily,
  fontUrl,
  onChange,
  onInvalidChange,
}: FontSelectorProps) {
  const { t } = useTranslation("trackoffice");
  const choice = emailFontChoice({ fontFamily, fontUrl });
  const [isCustom, setIsCustom] = useState(choice.kind === "custom");
  const [customName, setCustomName] = useState(
    choice.kind === "custom" ? choice.name : "",
  );
  const customFamily = googleFontFamily(customName);

  const value = isCustom
    ? "custom"
    : choice.kind === "default"
      ? "default"
      : `${choice.kind}:${choice.name}`;

  const onSelect = (selected: string) => {
    setIsCustom(selected === "custom");
    onInvalidChange(selected === "custom" && !customFamily);
    if (selected === "custom") {
      if (customFamily) onChange(customGoogleFontTheme(customFamily));
      return;
    }
    if (selected === "default") {
      onChange({ fontFamily: null, fontUrl: null });
      return;
    }

    const separator = selected.indexOf(":");
    const kind = selected.slice(0, separator);
    const name = selected.slice(separator + 1);
    if (kind === "standard") {
      const font = STANDARD_EMAIL_FONTS.find((f) => f.name === name);
      if (font) onChange(standardFontTheme(font));
    } else {
      const font = GOOGLE_EMAIL_FONTS.find((f) => f.name === name);
      if (font) onChange(googleFontTheme(font));
    }
  };

  const onCustomNameChange = (name: string) => {
    setCustomName(name);
    const family = googleFontFamily(name);
    onInvalidChange(!family);
    if (family) onChange(customGoogleFontTheme(family));
  };

  return (
    <div className="w-full flex flex-col gap-3">
      <div>
        <div className="font-medium text-sm">{title}</div>
        <div className="text-xs text-muted-foreground mt-1">{description}</div>
      </div>

      <Select value={value} onValueChange={onSelect}>
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="default">
            {t("settings.templates.fonts.default", "System font (default)")}
          </SelectItem>
          <SelectSeparator />
          <SelectGroup>
            <SelectLabel>
              {t("settings.templates.fonts.standard", "Standard fonts")}
            </SelectLabel>
            {STANDARD_EMAIL_FONTS.map((font) => (
              <SelectItem
                key={font.name}
                value={`standard:${font.name}`}
                style={{ fontFamily: font.fontFamily }}
              >
                {font.name}
              </SelectItem>
            ))}
          </SelectGroup>
          <SelectSeparator />
          <SelectGroup>
            <SelectLabel>Google Fonts</SelectLabel>
            {GOOGLE_EMAIL_FONTS.map((font) => (
              <SelectItem key={font.name} value={`google:${font.name}`}>
                {font.name}
              </SelectItem>
            ))}
            <SelectItem value="custom">
              {t("settings.templates.fonts.other", "Other Google font…")}
            </SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>

      {isCustom && (
        <div className="flex flex-col gap-1">
          <Input
            value={customName}
            onChange={(e) => onCustomNameChange(e.target.value)}
            placeholder={t(
              "settings.templates.fonts.other-placeholder",
              "Name on fonts.google.com, e.g. Roboto Condensed",
            )}
            className="h-8 text-sm"
          />
          {customName.trim() && !customFamily && (
            <div className="text-xs text-destructive">
              {t(
                "settings.templates.fonts.other-invalid",
                "This font isn't on Google Fonts: check its name on fonts.google.com.",
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

type BorderRadiusValue = "none" | "low" | "default" | "high";

interface BorderRadiusSelectorProps {
  title: string;
  description?: string;
  value: BorderRadiusValue;
  onChange: (value: BorderRadiusValue) => void;
}

function BorderRadiusSelector({
  title,
  description,
  value,
  onChange,
}: BorderRadiusSelectorProps) {
  const { t } = useTranslation("trackoffice");
  const options: { value: BorderRadiusValue; label: string; radius: string }[] =
    [
      {
        value: "none",
        label: t("settings.templates.radius.none", "None"),
        radius: "",
      },
      {
        value: "low",
        label: t("settings.templates.radius.low", "Low"),
        radius: "rounded-xs",
      },
      {
        value: "default",
        label: t("settings.templates.radius.medium", "Medium"),
        radius: "rounded-md",
      },
      {
        value: "high",
        label: t("settings.templates.radius.high", "High"),
        radius: "rounded-xl",
      },
    ];

  return (
    <div className="w-full flex flex-col gap-3">
      <div>
        <div className="font-medium text-sm">{title}</div>
        {description && (
          <div className="text-xs text-muted-foreground mt-1">
            {description}
          </div>
        )}
      </div>

      <div className="flex gap-2">
        {options.map((option) => (
          <div
            key={option.value}
            className={cn(
              "flex-1 cursor-pointer select-none border border-border/50 rounded-lg p-2 transition-all",
              value === option.value
                ? "border-primary bg-primary/5"
                : "hover:border-border",
            )}
            onClick={() => onChange(option.value)}
          >
            <div className="flex flex-col items-center gap-2">
              <div
                className={cn("py-3 px-6 border border-text", option.radius)}
              />
              <span className="text-xs font-medium">{option.label}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface ColorSwitcherProps {
  title: string;
  description?: string;
  value: string;
  onChange: (color: string) => void;
  type: "background" | "card" | "simple";
}

function ColorSwitcher({
  title,
  description,
  value,
  onChange,
  type,
}: ColorSwitcherProps) {
  const picker = useRef<HTMLInputElement>(null);

  const onClick = () => {
    picker.current?.click();
  };

  return (
    <div
      className="w-full flex gap-3 items-center justify-between cursor-pointer select-none"
      onClick={onClick}
    >
      <input
        ref={picker}
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="opacity-0 absolute pointer-events-none"
      />

      <div className="flex-1">
        <div className="font-medium text-sm">{title}</div>
        {description && (
          <div className="text-xs text-muted-foreground mt-1">
            {description}
          </div>
        )}
      </div>

      <div className="size-8 border border-border/50 rounded-md hover:scale-105 transition-transform">
        {type === "background" ? (
          <div
            className="w-full h-full rounded-sm relative"
            style={{ backgroundColor: value }}
          >
            <div
              className="absolute inset-2 rounded-xs border border-border/20 shadow-sm"
              style={{ backgroundColor: "#ffffff" }}
            />
          </div>
        ) : type === "simple" ? (
          <div
            className="w-full h-full rounded-sm"
            style={{ backgroundColor: value }}
          />
        ) : (
          <div
            className="w-full h-full rounded-sm p-1"
            style={{ backgroundColor: "#ffffff" }}
          >
            <div
              className="w-full h-full rounded border border-border/20 shadow-sm"
              style={{ backgroundColor: value }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

interface SettingsCardProps {
  children: React.ReactNode;
  className?: string;
  width?: "full" | "half";
}

function SettingsCard({
  children,
  className,
  width = "full",
}: SettingsCardProps) {
  return (
    <div
      className={cn(
        "bg-muted/10 rounded-xl p-3 border border-border/50 flex flex-row",
        width === "half" ? "flex-1" : "w-full",
        className,
      )}
    >
      {children}
    </div>
  );
}
